import { useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  removeCreateProjectErrorFields,
  saveWorkingProjectSessionByField,
  setCreateProjectErrorFields,
} from '@/store/slices/project';
import {
  getIsLoadingWorkingProject,
  getWorkingProject,
  selectCreateProjectErrors,
} from '@/store/slices/project/selectors';
import { getActiveChannel } from '@/store/slices/user/selectors';
import InputWithOverlay from '@ui/input-with-overlay/input-with-overlay';
import toastTrigger from '@ui/toast/toast-trigger';
import Text from '@ui/text/text/text';
import OptionalComponent from '@ui/optional-component/optional-component';
import WaitingIndicator from '@ui/waiting-indicator/waiting-indicator';
import { updateTranslateKey } from '@/utils/update-translation-key';
import { useParams } from 'react-router-dom';
import { getDefaultLanguage } from '@/utils/get-default-language';
import useHasPermission from '@/utils/authentication/use-has-permission';
import { GENERAL_EDITABLE_PERMISSIONS } from '@/types/permissions.types';

import styles from './edit-session-title.module.css';
import { EToastTypes } from '@ui/toast/toast.types';

type EditSessionTitleProps = {
  defaultName?: string;
  placeholder?: string;
  absolutePositionError?: boolean;
};

const EditSessionTitle = ({
  absolutePositionError = true,
  defaultName,
  placeholder = 'Give your session a name',
}: EditSessionTitleProps) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const createProjectErrors = useAppSelector(selectCreateProjectErrors);
  const workingProject = useAppSelector(getWorkingProject);
  const userActiveChannel = useAppSelector(getActiveChannel);
  const loadingProject = useAppSelector(getIsLoadingWorkingProject);
  const { hasPermission } = useHasPermission({ allowedRoles: GENERAL_EDITABLE_PERMISSIONS });

  const params = useParams();
  const sessionUuid = params?.sessionUuid;
  const language = params?.language || 'en';

  const session = (sessionUuid && workingProject?.sessions?.find((s) => s.uuid === sessionUuid)) || null;

  const defaultLanguage = session?.default_language ?? getDefaultLanguage(workingProject);

  const dispatch = useAppDispatch();

  useEffect(() => {
    // clear session name errors on unmount
    return () => {
      dispatch(removeCreateProjectErrorFields({ sessionName: null }));
    };
  }, [dispatch]);

  const handleBlur = async (e: React.FocusEvent<HTMLInputElement>) => {
    if (!workingProject || !userActiveChannel || !session) {
      return;
    }

    const title = e.target.value.trim();

    if (!title.length) {
      dispatch(setCreateProjectErrorFields({ sessionName: 'Session name required' }));
      return;
    }

    const sessionNameExists = workingProject.sessions.find((s) => {
      return s.title.base.trim().toLowerCase() === title.toLowerCase() && s.uuid !== session.uuid;
    });

    if (sessionNameExists) {
      dispatch(setCreateProjectErrorFields({ sessionName: 'Session name already exists' }));
      return;
    }

    dispatch(setCreateProjectErrorFields(null));

    if (title.trim().length) {
      try {
        const newTranslateKey = updateTranslateKey({
          translateString: session.title,
          input: title,
          language: language,
          baseLanguage: defaultLanguage,
        });
        dispatch(
          saveWorkingProjectSessionByField({
            uuid: session.uuid,
            title: newTranslateKey,
          })
        );
      } catch (error) {
        console.error('Error validating session name', error);
        toastTrigger('There was an error validating the session name.', {
          type: EToastTypes.error,
          autoClose: 10000,
          description: 'Please refresh and try again.',
        });
      }
    }
  };

  if (!session) return null;
  return (
    <>
      <InputWithOverlay
        data-component="EditSessionTitle"
        design={null}
        designSize="small"
        typography="H4"
        inputRef={inputRef}
        status={createProjectErrors?.sessionName ? 'error' : undefined}
        inputProps={{
          defaultValue: defaultName ?? session?.title?.[language] ?? session?.title?.base ?? '',
          placeholder,
          className: `padding-0 radius-4 ${styles.sessionTitleInput}`,
          disabled: !hasPermission,
          onBlur: handleBlur,
          onFocus: () => {
            if (inputRef.current) {
              // reset scroll position to the left when focused so the text position matches the overlay
              inputRef.current.scrollLeft = 0;
            }
          },
        }}
        renderInnerContent={() => {
          if (!loadingProject) return null;
          return (
            <OptionalComponent display delayRender={300}>
              <div className="flex fd-row jc-center ai-center width-100-percent height-32">
                <WaitingIndicator />
              </div>
            </OptionalComponent>
          );
        }}
      />
      <OptionalComponent display={!!createProjectErrors?.sessionName}>
        <Text
          className={`S3 ${absolutePositionError ? 'position-absolute top-100-percent margin-t--2' : ''}`}
          color="var(--semantic-red-500)"
        >
          {createProjectErrors?.sessionName || ''}
        </Text>
      </OptionalComponent>
    </>
  );
};

export default EditSessionTitle;
